import { useCallback, useEffect, useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  bulkSetTeamCommunicatorsThunk,
  fetchTeamsByRoomId,
} from '@/redux/asyncThunks/team'
import { Team, TeamCommunicator } from '@/redux/types/team'
import { RoomMember, GameRoomStatus } from '@/redux/types/room'
import { useGamePhase } from '@/hooks/useGamePhase'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import { getTeamCommunicators } from '@/api/team'
import { toast } from 'sonner'
import { fetchTimerConfigurationAPIThunk } from '@/redux/asyncThunks/timerConfiguration'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import ConfirmationModal from '@/components/OGE/OGEDeletionModal'
import { RoundInfo, RoundTabs } from '@/components/settings/TeamMode/RoundTabs'

interface TeamCommunicatorModalProps {
  isOpen: boolean
  onClose: () => void
  roomId: string
}

export function TeamCommunicatorModal({
  isOpen,
  onClose,
  roomId,
}: TeamCommunicatorModalProps) {
  const dispatch = useAppDispatch()
  const {
    currentRoom,
    roomMembers,
    isLoading: roomLoading,
  } = useAppSelector((state) => state.room)
  const { teams, isLoading: teamsLoading } = useAppSelector(
    (state) => state.team
  )
  const { currentRound } = useGamePhase()
  const { config } = useAppSelector((state) => state.timerConfiguration)
  const [selectedRound, setSelectedRound] = useState<number>(1)
  const [rounds, setRounds] = useState<RoundInfo[]>([])
  const [communicators, setCommunicators] = useState<
    Map<number, Map<number, RoomMember | null>>
  >(new Map())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showConfirmClose, setShowConfirmClose] = useState(false)

  const [pendingChanges, setPendingChanges] = useState<
    Map<number, Map<number, number | null>>
  >(new Map())
  const [originalCommunicators, setOriginalCommunicators] = useState<
    Map<number, Map<number, RoomMember | null>>
  >(new Map())

  const teamMembers = useMemo(() => {
    const membersByTeam = new Map<number, RoomMember[]>()
    teams.forEach((team: Team) => {
      const teamMembersList = roomMembers.filter(
        (member: RoomMember) => member.teamId === team.id
      )
      membersByTeam.set(team.id, teamMembersList)
    })
    return membersByTeam
  }, [teams, roomMembers])

  useEffect(() => {
    if (isOpen && roomId) {
      dispatch(getRoomDetails(roomId))
      dispatch(fetchTimerConfigurationAPIThunk(roomId))
      dispatch(fetchTeamsByRoomId(roomId))
      dispatch(getRoomMembers(roomId))
    }
  }, [isOpen, roomId, dispatch])

  useEffect(() => {
    if (config?.roundConfigurations) {
      const roundsData: RoundInfo[] = config.roundConfigurations
        .map((roundConfig: any) => ({
          id: roundConfig.roundId,
          round_number: roundConfig.roundNumber,
          status: roundConfig.status,
        }))
        .filter(
          (round: RoundInfo) => round.round_number != null || round.id != null
        )
      setRounds(roundsData)
      if (roundsData.length > 0) {
        const firstRoundNumber = roundsData[0].round_number ?? roundsData[0].id
        if (firstRoundNumber != null) {
          setSelectedRound(firstRoundNumber)
        }
      }
    }
  }, [config, currentRound])

  useEffect(() => {
    if (isOpen && roomId && teams.length > 0 && roomMembers.length > 0) {
      const fetchCommunicators = async () => {
        setLoading(true)
        try {
          const communicatorsMap = new Map<
            number,
            Map<number, RoomMember | null>
          >()

          for (const team of teams) {
            const teamCommunicatorsMap = new Map<number, RoomMember | null>()
            try {
              const comms = await getTeamCommunicators(team.id)
              comms.forEach((comm: TeamCommunicator) => {
                if (!comm.communicator) return
                const member = roomMembers.find(
                  (m: RoomMember & { id?: number }) => {
                    const roomMemberId = m.id
                    return (
                      roomMemberId?.toString() === comm.communicator?.toString()
                    )
                  }
                )

                if (member) {
                  teamCommunicatorsMap.set(comm.round_number, member)
                }
              })
            } catch (error) {
              console.debug(
                `No communicators found for team ${team.id} (this is normal if not set yet)`
              )
            }
            communicatorsMap.set(team.id, teamCommunicatorsMap)
          }

          setCommunicators(communicatorsMap)
          setOriginalCommunicators(communicatorsMap)
          setPendingChanges(new Map())
        } catch (error) {
          console.error('Failed to fetch communicators:', error)
        } finally {
          setLoading(false)
        }
      }
      fetchCommunicators()
    }
  }, [isOpen, roomId, teams, roomMembers])

  const handleCommunicatorChange = useCallback(
    (teamId: number, roundNumber: number, communicatorId: number | null) => {
      const round = rounds.find((r) => r.round_number === roundNumber)
      if (!round) {
        console.error(`Round ${roundNumber} not found`)
        return
      }

      if (round.status === ActivityState.InProgress) {
        toast.error(
          `Cannot change communicator for Round ${roundNumber} - round has started`
        )
        return
      }

      setPendingChanges((prev) => {
        const newMap = new Map(prev)
        const teamMap = new Map(newMap.get(teamId) || new Map())
        teamMap.set(roundNumber, communicatorId)
        newMap.set(teamId, teamMap)
        return newMap
      })

      if (communicatorId !== null) {
        const member = teamMembers
          .get(teamId)
          ?.find((m: RoomMember & { id?: number }) => {
            const roomMemberId = m.id
            return roomMemberId?.toString() === communicatorId.toString()
          })

        if (member) {
          setCommunicators((prev) => {
            const newMap = new Map(prev)
            const teamMap = new Map(newMap.get(teamId) || new Map())
            teamMap.set(roundNumber, member)
            newMap.set(teamId, teamMap)
            return newMap
          })
        }
      } else {
        setCommunicators((prev) => {
          const newMap = new Map(prev)
          const teamMap = new Map(newMap.get(teamId) || new Map())
          teamMap.set(roundNumber, null)
          newMap.set(teamId, teamMap)
          return newMap
        })
      }
    },
    [rounds, teamMembers]
  )

  const handleApplyRound1ToAll = useCallback(() => {
    if (rounds.length <= 1) return

    const round1 = rounds.find((r) => r.round_number === 1)
    if (!round1) return

    teams.forEach((team) => {
      const round1Member = communicators.get(team.id)?.get(1) ?? null
      const round1MemberId =
        (round1Member as RoomMember & { id?: number })?.id ?? null

      rounds.forEach((round) => {
        if (round.round_number === 1) return
        if (round.status === ActivityState.InProgress) return

        handleCommunicatorChange(team.id, round.round_number, round1MemberId)
      })
    })

    toast.success('Round 1 communicators applied to all rounds')
  }, [rounds, teams, communicators, handleCommunicatorChange])

  const getCommunicatorForTeamRound = useCallback(
    (teamId: number, roundNumber: number): RoomMember | null => {
      return communicators.get(teamId)?.get(roundNumber) || null
    },
    [communicators]
  )

  const isRoundStarted = (roundNumber: number): boolean => {
    const round = rounds.find((r) => r.round_number === roundNumber)
    return round?.status === ActivityState.InProgress || false
  }

  const getRoundStatus = (roundNumber: number): string => {
    const round = rounds.find((r) => r.round_number === roundNumber)
    return round?.status || ActivityState.NotStarted
  }

  const isGameStarted = currentRoom?.status !== GameRoomStatus.NOT_STARTED

  const validateBeforeSave = useCallback((): string | null => {
    if (!isGameStarted) {
      for (const team of teams) {
        const round1Communicator = communicators.get(team.id)?.get(1)
        if (!round1Communicator) {
          return `Round 1 communicator is required for team "${team.name}"`
        }
      }
    }
    return null
  }, [teams, communicators, isGameStarted])

  const hasPendingChanges = useCallback(() => {
    if (pendingChanges.size === 0) return false

    for (const [teamId, teamChanges] of pendingChanges.entries()) {
      for (const [roundNumber, communicatorId] of teamChanges.entries()) {
        const original = originalCommunicators.get(teamId)?.get(roundNumber)
        const originalId = (original as RoomMember & { id?: number })?.id
        if (communicatorId !== originalId) {
          return true
        }
      }
    }
    return false
  }, [pendingChanges, originalCommunicators])

  const handleSave = useCallback(async () => {
    const validationError = validateBeforeSave()
    if (validationError) {
      toast.error(validationError)
      return
    }

    const assignments: Array<{
      team_id: number
      game_round_id: number
      communicator_id: number
    }> = []

    for (const [teamId, teamChanges] of pendingChanges.entries()) {
      for (const [roundNumber, communicatorId] of teamChanges.entries()) {
        if (communicatorId === null) continue

        const round = rounds.find((r) => r.round_number === roundNumber)
        if (!round) continue

        assignments.push({
          team_id: teamId,
          game_round_id: round.id,
          communicator_id: communicatorId,
        })
      }
    }

    if (assignments.length === 0) {
      toast.info('No changes to save')
      return
    }

    setSaving(true)
    try {
      const response = await dispatch(
        bulkSetTeamCommunicatorsThunk({
          roomId,
          assignments,
        })
      ).unwrap()

      const successCount = response.successful_count
      const failCount = response.failed_count

      setOriginalCommunicators(new Map(communicators))
      setPendingChanges(new Map())

      if (failCount > 0) {
        toast.warning(
          `Saved ${successCount} communicator(s), ${failCount} failed. Check console for details.`
        )
        console.error('Failed assignments:', response.failed_assignments)
      } else {
        toast.success(`Successfully saved ${successCount} communicator(s)`)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save communicators'
      toast.error(`Failed to save: ${errorMessage}`)
      console.error('Failed to save communicators:', error)
    } finally {
      setSaving(false)
    }
  }, [
    pendingChanges,
    rounds,
    originalCommunicators,
    communicators,
    validateBeforeSave,
    dispatch,
    roomId,
    isGameStarted,
    teams,
  ])

  const handleClose = useCallback(() => {
    if (hasPendingChanges()) {
      setShowConfirmClose(true)
    } else {
      onClose()
    }
  }, [hasPendingChanges, onClose])

  const handleConfirmClose = useCallback(() => {
    setPendingChanges(new Map())
    setCommunicators(new Map(originalCommunicators))
    onClose()
  }, [originalCommunicators, onClose])

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          aria-describedby={undefined}
          className='w-[95vw] max-w-6xl h-[90vh] p-0 flex flex-col'
        >
          <DialogHeader className='flex-shrink-0 px-6 py-4 border-b'>
            <DialogTitle>Team Communicators</DialogTitle>
            <p className='mt-1 text-sm text-gray-600'>
              Assign team communicators for each round. Round 1 is required
              before the game starts. Once a round has started, its communicator
              cannot be changed.
            </p>
          </DialogHeader>

          <RoundTabs
            loading={loading}
            teamsLoading={teamsLoading}
            roomLoading={roomLoading}
            selectedRound={selectedRound}
            setSelectedRound={setSelectedRound}
            rounds={rounds}
            teams={teams}
            teamMembers={teamMembers}
            getCommunicatorForTeamRound={getCommunicatorForTeamRound}
            handleCommunicatorChange={handleCommunicatorChange}
            handleApplyRound1ToAll={handleApplyRound1ToAll}
            isRoundStarted={isRoundStarted}
            getRoundStatus={getRoundStatus}
            isGameStarted={isGameStarted}
            handleClose={handleClose}
            handleSave={handleSave}
            saving={saving}
            hasPendingChanges={hasPendingChanges}
          />
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={handleConfirmClose}
        title='Unsaved Changes'
        description='You have unsaved changes. Are you sure you want to close? All unsaved changes will be lost.'
        confirmButtonText='Close Without Saving'
        cancelButtonText='Cancel'
      />
    </>
  )
}
